import { Close } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  Grid,
  Typography,
  useTheme,
} from '@mui/material';
import { Loading } from '@/components/atoms/Loading';
import { usePartnerShiftsConfigMutation } from '@/hooks/usePartnerShiftsConfigMutation';
import { useShiftsQuery } from '@/hooks/useShiftsQuery';
import { useSnackbar } from 'notistack';
import { ReportsService } from '@/openapi';
import type { ChangeEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/util/queryKeys';

interface ConfigParnterShiftsProps {
  hideModal: () => void;
}

export const ConfigParnterShifts: React.FC<ConfigParnterShiftsProps> = ({
  hideModal,
}) => {
  const theme = useTheme();
  const snackbar = useSnackbar();

  const { data: shifts, isLoading: shiftsLoading } = useShiftsQuery();
  const { data: shiftsConfigResponse, isLoading: configLoading } = useQuery({
    queryKey: queryKeys.shifts.config(),
    queryFn: () => ReportsService.reportsServiceGetShiftTotalsConfig(),
  });
  const partnerShiftsConfigMutation = usePartnerShiftsConfigMutation();
  const shiftsConfig = shiftsConfigResponse?.data;

  const onCheckInclude = async (
    event: ChangeEvent<HTMLInputElement>,
    shiftId: number
  ) => {
    if (!shiftsConfig) {
      return;
    }
    try {
      if (event.target.checked) {
        if (!shiftsConfig.displayedShifts.includes(shiftId)) {
          const newConfig = shiftsConfig.displayedShifts
            .concat(shiftId)
            .sort((a, b) => a - b);
          await partnerShiftsConfigMutation.mutateAsync({
            displayedShifts: newConfig,
          });
        }
      } else {
        if (shiftsConfig.displayedShifts.includes(shiftId)) {
          const newConfig = shiftsConfig.displayedShifts.filter(
            (shift) => shift !== shiftId
          );
          await partnerShiftsConfigMutation.mutateAsync({
            displayedShifts: newConfig,
          });
        }
      }
    } catch {
      snackbar.enqueueSnackbar('Assignment failed to update', {
        variant: 'error',
      });
    }
  };

  return (
    <Dialog
      open
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        width: '100%',
      }}>
      <Grid
        sx={{
          display: 'flex',
          justifyContent: 'right',
          height: theme.spacing(6),
        }}>
        <Button onClick={hideModal}>
          <Close />
        </Button>
      </Grid>
      <Box
        sx={{
          px: 6,
          pt: 1,
          pb: 2,
          width: theme.spacing(40),
        }}>
        <Typography
          variant="h2"
          sx={{
            textAlign: 'center',
          }}>
          Configure Shifts
        </Typography>
        <Box
          sx={{
            p: 2,
          }}
        />
        <Grid
          container
          spacing={2}
          sx={{
            alignItems: 'center',
            pl: 1,
            pb: 2,
            width: '100%',
          }}>
          <Grid size={8}>
            <Typography variant="h4">Shift</Typography>
          </Grid>
          <Grid
            sx={{
              display: 'flex',
              justifyContent: 'center',
            }}
            size={4}>
            <Typography variant="h4">Included</Typography>
          </Grid>
        </Grid>
        {shifts && shiftsConfig ? (
          <Box
            sx={{
              border: 2,
              borderColor: theme.palette.divider,
              mr: -3,
              maxHeight: theme.spacing(60),
              overflowY: 'scroll',
            }}>
            {shifts?.map((shift) => (
              <Box
                key={shift.id}
                sx={{
                  p: 1,
                }}>
                <Grid
                  container
                  spacing={2}
                  sx={{
                    alignItems: 'center',
                    width: '100%',
                  }}>
                  <Grid size={8}>
                    <Typography>{shift.description}</Typography>
                  </Grid>
                  <Grid
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                    size={4}>
                    <Checkbox
                      checked={shiftsConfig?.displayedShifts.includes(shift.id)}
                      onChange={(event) => onCheckInclude(event, shift.id)}
                    />
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Box>
        ) : shiftsLoading || configLoading ? (
          <Loading />
        ) : (
          'An error occured while loading the schedule.'
        )}
        <Box
          sx={{
            p: 1,
          }}
        />
        <Grid
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
          <Button
            variant="contained"
            onClick={hideModal}
            sx={{ width: theme.spacing(10) }}>
            Close
          </Button>
        </Grid>
      </Box>
    </Dialog>
  );
};
